/**
 * AuthPage.tsx — the /auth surface: login · register · forgot · reset · verify.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: one auth front door for every role — the post-sign-in redirect decides
 * where a user lands, never the URL path. An explicit `?next=` query param
 * wins on LOGIN when it passes `safeNextForRole` (internal path + role-
 * compatible — checkout deep links rely on it; a `?next=/admin` for a patient
 * does not); otherwise the destination is role-aware — each role lands on its
 * own home (homeForRole), so an admin signing in at /auth doesn't land on the
 * patient dashboard. REGISTER ignores `next` entirely: registration mints
 * PATIENT accounts, and a fresh registrant must never be routed toward a
 * staff surface. Standalone register is emailVerified:false until they follow
 * the emailed link, so they land on check-email — not the dashboard. Journey
 * checkout register is already verified (OTP) and does not use this page.
 * Modes are chosen by `?mode=`; reset-confirm and verify-email read their
 * `?token=`. All flows call the identity gateway and render retryable errors
 * (TA-06). Already-signed-in users are bounced to their destination the same
 * (safe-next) way.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ZoneExitLink } from '../tracking/ZoneExitLink';
import { ApiError } from '../api/client';
import { acceptInvite, confirmPasswordReset, requestPasswordReset, verifyEmail } from '../api/identity';
import { Alert, Button, Card, ChevronLeftIcon, ErrorLine, Field, Help, PageFrame, PasswordInput, Spinner, TextInput, Title } from '../ui/kit';
import { useAuth } from './AuthContext';
import { setToken } from './session';
import { LoginForm, MIN_PASSWORD, RegisterForm } from './forms';
import { destinationAfterAuth, homeForRole } from './roleHome';
import { CheckEmailCard } from './CheckEmailCard';
import type { Me } from '../api/types';

type Mode = 'login' | 'register' | 'forgot' | 'reset' | 'verify' | 'invite' | 'check-email';

function readMode(raw: string | null): Mode {
  return raw === 'register' || raw === 'forgot' || raw === 'reset' || raw === 'verify' || raw === 'invite' || raw === 'check-email'
    ? raw
    : 'login';
}

export function AuthPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignedIn, status, me } = useAuth();
  const mode = readMode(params.get('mode'));
  // An explicit `?next=` wins on login when safeNextForRole allows it
  // (internal + role-compatible). Absent/unsafe, the destination is
  // role-aware — resolved once we know who signed in.
  const explicitNext = params.get('next');
  const token = params.get('token') || '';

  // A signed-in user has no business on the sign-in screens. Unverified
  // patients stay on check-email; verified patients bounce to their home.
  useEffect(() => {
    if (status !== 'ready' || !isSignedIn || !me) return;
    if (mode === 'check-email') {
      if (me.emailVerified) navigate(homeForRole(me.role), { replace: true });
      return;
    }
    if (mode === 'login' || mode === 'register') {
      navigate(destinationAfterAuth(me, mode === 'login' ? explicitNext : null), { replace: true });
    }
  }, [status, isSignedIn, me, mode, explicitNext, navigate]);

  const onLoginSuccess = (principal: Me) =>
    navigate(destinationAfterAuth(principal, explicitNext), { replace: true });
  const onRegisterSuccess = (principal: Me) => navigate(destinationAfterAuth(principal), { replace: true });
  const searchSuffix = useMemo(() => {
    const p = new URLSearchParams(location.search);
    p.delete('mode');
    const rest = p.toString();
    return rest ? `&${rest}` : '';
  }, [location.search]);
  // The REGISTER link additionally drops `next` — the staff path (or any deep
  // link) never even rides along into the registration screen. Login/forgot
  // links keep it (searchSuffix above).
  const registerSearchSuffix = useMemo(() => {
    const p = new URLSearchParams(location.search);
    p.delete('mode');
    p.delete('next');
    const rest = p.toString();
    return rest ? `&${rest}` : '';
  }, [location.search]);

  return (
    <PageFrame narrow>
      {mode === 'login' ? (
        <>
          <Title>Welcome back</Title>
          <Help>Sign in to manage your plan, orders and delivery details.</Help>
          <Card>
            <LoginForm onSuccess={onLoginSuccess} />
          </Card>
          <nav style={{ marginTop: 'var(--gps-s-4)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Link to={`/auth?mode=register${registerSearchSuffix}`} style={{ color: 'var(--gps-link)' }}>
              New to GPS Clinics? Create an account
            </Link>
            <Link to={`/auth?mode=forgot${searchSuffix}`} style={{ color: 'var(--gps-link)' }}>
              Forgotten your password?
            </Link>
          </nav>
        </>
      ) : null}

      {mode === 'register' ? (
        <>
          <Title>Create your account</Title>
          <Help>It takes a moment — your account keeps your plan and orders in one place.</Help>
          <Card>
            <RegisterForm onSuccess={onRegisterSuccess} />
          </Card>
          <nav style={{ marginTop: 'var(--gps-s-4)' }}>
            <Link to={`/auth?mode=login${searchSuffix}`} style={{ color: 'var(--gps-link)' }}>
              Already have an account? Sign in
            </Link>
          </nav>
        </>
      ) : null}

      {mode === 'forgot' ? <ForgotPassword /> : null}
      {mode === 'reset' ? <ResetConfirm token={token} /> : null}
      {mode === 'check-email' ? <CheckEmail /> : null}
      {mode === 'verify' ? <VerifyEmail token={token} /> : null}
      {mode === 'invite' ? <InviteAccept token={token} /> : null}
    </PageFrame>
  );
}

/**
 * invite/accept — a clinician/admin activates their account by choosing a password
 * from their single-use invite token. On success they are signed in and taken
 * straight to their workspace (role comes from the invite, never the URL).
 */
function InviteAccept({ token }: { token: string }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState('');
  const [state, setState] = useState<'idle' | 'busy'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (state === 'busy') return;
    if (!token) {
      setError('This invite link is missing its token. Ask your admin to resend the invite.');
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setError(null);
    setState('busy');
    try {
      const session = await acceptInvite(token, password);
      setToken(session.sessionToken);
      await refresh();
      navigate(homeForRole(session.role), { replace: true });
    } catch (err) {
      setState('idle');
      setError(
        err instanceof ApiError && err.status === 400
          ? 'That invite is invalid or has already been used. Ask your admin to resend it.'
          : 'Sorry — we could not activate your account. Please try again.',
      );
    }
  }

  return (
    <>
      <Title>Set up your account</Title>
      <Help>You&rsquo;ve been invited to the GPS Clinics team. Choose a password to get started.</Help>
      <Card>
        <form onSubmit={handleSubmit} aria-label="Accept invite" noValidate>
          <Field label="Choose a password" htmlFor="invite-password" hint={`At least ${MIN_PASSWORD} characters.`}>
            <PasswordInput
              id="invite-password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {error ? <ErrorLine>{error}</ErrorLine> : null}
          <Button type="submit" variant="cta" disabled={state === 'busy'} arrow={state !== 'busy'}>
            {state === 'busy' ? 'Setting up…' : 'Activate my account'}
          </Button>
        </form>
      </Card>
    </>
  );
}

/** reset/request — always shows the same confirmation (no account enumeration). */
function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (state === 'busy') return;
    if (!email.includes('@')) {
      setError('Enter the email address for your account.');
      return;
    }
    setError(null);
    setState('busy');
    try {
      await requestPasswordReset(email.trim());
      setState('done');
    } catch {
      setState('idle');
      setError('Sorry — we could not send the reset email. Please try again.');
    }
  }

  if (state === 'done') {
    return (
      <>
        <Title>Check your email</Title>
        <Alert tone="success" role="status">
          If an account exists for that address, we&rsquo;ve sent a link to reset your password.
        </Alert>
        <p style={{ marginTop: 'var(--gps-s-4)' }}>
          <Link to="/auth?mode=login" style={{ color: 'var(--gps-link)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ChevronLeftIcon size={14} />
            Back to sign in
          </Link>
        </p>
      </>
    );
  }

  return (
    <>
      <Title>Reset your password</Title>
      <Help>Enter your email and we&rsquo;ll send you a link to set a new password.</Help>
      <Card>
        <form onSubmit={handleSubmit} aria-label="Reset password" noValidate>
          <Field label="Email address" htmlFor="forgot-email">
            <TextInput id="forgot-email" type="email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {error ? <ErrorLine>{error}</ErrorLine> : null}
          <Button type="submit" variant="cta" disabled={state === 'busy'} arrow={state !== 'busy'}>
            {state === 'busy' ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      </Card>
    </>
  );
}

/** reset/confirm — set a new password from the emailed token. */
function ResetConfirm({ token }: { token: string }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (state === 'busy') return;
    if (!token) {
      setError('This reset link is missing its token. Request a new link.');
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setError(null);
    setState('busy');
    try {
      await confirmPasswordReset(token, password);
      setState('done');
    } catch (err) {
      setState('idle');
      setError(
        err instanceof ApiError && err.status === 400
          ? 'That reset link has expired. Request a new one.'
          : 'Sorry — we could not reset your password. Please try again.',
      );
    }
  }

  if (state === 'done') {
    return (
      <>
        <Title>Password updated</Title>
        <Alert tone="success" role="status">
          Your password has been changed. You can now sign in with it.
        </Alert>
        <div style={{ marginTop: 'var(--gps-s-4)', maxWidth: '16rem' }}>
          <Button type="button" variant="cta" arrow onClick={() => navigate('/auth?mode=login')}>
            Sign in
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Title>Choose a new password</Title>
      <Card>
        <form onSubmit={handleSubmit} aria-label="Set new password" noValidate>
          <Field label="New password" htmlFor="reset-password" hint={`At least ${MIN_PASSWORD} characters.`}>
            <PasswordInput id="reset-password" value={password} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />
          </Field>
          {error ? <ErrorLine>{error}</ErrorLine> : null}
          <Button type="submit" variant="cta" disabled={state === 'busy'} arrow={state !== 'busy'}>
            {state === 'busy' ? 'Saving…' : 'Save new password'}
          </Button>
        </form>
      </Card>
    </>
  );
}

/** Standalone /auth register: wait here until the emailed verify link is followed. */
function CheckEmail() {
  return (
    <>
      <Title>Check your email</Title>
      <CheckEmailCard />
    </>
  );
}

/** verify-email — confirm the token on mount, with a retry on failure. */
function VerifyEmail({ token }: { token: string }) {
  const { refresh } = useAuth();
  const [state, setState] = useState<'busy' | 'done' | 'error'>('busy');

  async function run() {
    setState('busy');
    if (!token) {
      setState('error');
      return;
    }
    try {
      await verifyEmail(token);
      await refresh();
      setState('done');
    } catch {
      setState('error');
    }
  }

  useEffect(() => {
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (state === 'busy') {
    return (
      <>
        <Title>Verifying your email…</Title>
        <Spinner label="Verifying your email…" />
      </>
    );
  }

  if (state === 'done') {
    return (
      <>
        <Title>Email verified</Title>
        <Alert tone="success" role="status">
          Thanks — your email address is confirmed.
        </Alert>
        <p style={{ marginTop: 'var(--gps-s-4)' }}>
          <ZoneExitLink to="/patients" style={{ color: 'var(--gps-link)' }}>
            Go to your dashboard
          </ZoneExitLink>
        </p>
      </>
    );
  }

  return (
    <>
      <Title>We couldn&rsquo;t verify that link</Title>
      <Alert tone="danger" role="alert">
        This verification link is invalid or has expired.
      </Alert>
      <div style={{ marginTop: 'var(--gps-s-4)', maxWidth: '16rem' }}>
        <Button type="button" variant="primary" onClick={() => void run()}>
          Try again
        </Button>
      </div>
      <p style={{ marginTop: 'var(--gps-s-3)' }}>
        <Link to="/auth?mode=check-email" style={{ color: 'var(--gps-link)' }}>
          Request a new link
        </Link>
      </p>
    </>
  );
}
